import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronDown,
  FileText,
  Filter,
  LayoutGrid,
  List,
  MoreVertical,
  Palette,
  Plus,
  Search,
  Store,
  Tag,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import client, { unwrap } from "@/api/client";
import { cn } from "@/lib/utils";
import { formatINR, initials } from "@/lib/format";
import { SOURCES, STAGES, STAGE_LABEL, propertyLine, sourceMeta, stageTone } from "@/components/enquiries/enquiryMeta";
import AddEnquiryDialog from "@/components/enquiries/AddEnquiryDialog";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const ALL = "all";
const PAGE_SIZE = 12;
const VIEW_KEY = "hi_enquiries_view";

const AVATAR_TONES = [
  "bg-accent/10 text-accent",
  "bg-primary/10 text-primary",
  "bg-success/10 text-success",
  "bg-chart-3/10 text-chart-3",
  "bg-warning/15 text-[hsl(41_60%_32%)]",
  "bg-[hsl(262_55%_60%/0.12)] text-[hsl(262_45%_52%)]",
  "bg-[hsl(212_70%_55%/0.12)] text-[hsl(212_60%_45%)]",
];

const STAT_CARDS = [
  { key: "total", label: "Total Enquiries", icon: UsersRound, tone: "bg-accent/10 text-accent" },
  { key: "QUALIFIED", label: "Qualified", icon: Filter, tone: "bg-warning/15 text-[hsl(41_60%_32%)]" },
  { key: "DESIGNING", label: "Designing", icon: Palette, tone: "bg-success/10 text-success" },
  { key: "QUOTATION", label: "Quotation", icon: FileText, tone: "bg-[hsl(262_55%_60%/0.12)] text-[hsl(262_45%_52%)]" },
];

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
const shortDate = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

function hashTone(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}

function readView() {
  try {
    return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "board";
  } catch {
    return "board";
  }
}

function Avatar({ name, size = "h-9 w-9" }) {
  return (
    <span className={cn("rounded-full font-serif text-[13px] flex items-center justify-center shrink-0", size, hashTone(name))}>
      {initials(name)}
    </span>
  );
}

function SourceTag({ source }) {
  const meta = sourceMeta(source);
  if (!meta) return <span className="text-muted-foreground">—</span>;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground min-w-0">
      <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{meta.value}</span>
    </span>
  );
}

function StagePill({ status }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap", stageTone(status).pill)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", stageTone(status).dot)} />
      {STAGE_LABEL[status] || status}
    </span>
  );
}

function EnquiryMenu({ e, onMove }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Actions for ${e.code || e.customerName}`}
          data-testid={`enquiry-card-menu-${e._id}`}
          onClick={(ev) => ev.stopPropagation()}
          className="p-1 -mr-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary shrink-0"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(ev) => ev.stopPropagation()}>
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Move to</DropdownMenuLabel>
        {STAGES.filter((s) => s.key !== e.status).map((s) => (
          <DropdownMenuItem key={s.key} data-testid={`move-enquiry-${e._id}-to-${s.key}`} onClick={() => onMove(e, s.key)}>
            <span className={cn("h-2 w-2 rounded-full mr-2", stageTone(s.key).dot)} />
            {s.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(`/customers/${e.customerId}`)}>View customer</DropdownMenuItem>
        {e.customerPhone && (
          <DropdownMenuItem asChild>
            <a href={`tel:${e.customerPhone}`}>Call {e.customerPhone}</a>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EnquiryCard({ e, onMove, dragging, onDragStart, onDragEnd }) {
  const navigate = useNavigate();
  const property = propertyLine(e);
  return (
    <div
      data-testid={`enquiry-card-${e._id}`}
      draggable
      onDragStart={(ev) => {
        ev.dataTransfer.effectAllowed = "move";
        ev.dataTransfer.setData("text/plain", e._id);
        onDragStart(e);
      }}
      onDragEnd={onDragEnd}
      onClick={() => navigate(`/customers/${e.customerId}`)}
      className={cn(
        "group bg-card border border-border rounded-xl p-3.5 cursor-grab active:cursor-grabbing shadow-[0_1px_2px_rgba(42,33,29,0.04)] hover:shadow-md hover:border-foreground/15 transition-all",
        dragging && "opacity-40",
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {e.code && <p className="font-mono-spec text-[11px] text-muted-foreground">{e.code}</p>}
          <p className="text-sm font-medium text-foreground truncate">{e.customerName}</p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{property || "No property details"}</p>
        </div>
        <Avatar name={e.customerName} />
        <EnquiryMenu e={e} onMove={onMove} />
      </div>
      {e.potentialValue ? <p className="mt-2 text-xs font-semibold text-accent">{formatINR(e.potentialValue)} potential</p> : null}
      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
        <SourceTag source={e.source} />
        <span className="inline-flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
          <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
          {formatDate(e.createdAt)}
        </span>
      </div>
    </div>
  );
}

function FilterSelect({ icon: Icon, value, onChange, testId, allLabel, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-full sm:w-auto sm:min-w-[150px] h-10 rounded-lg bg-card">
        <span className="flex items-center gap-2 truncate">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DateRangeFilter({ range, onChange }) {
  const label = range?.from
    ? range.to && range.to.getTime() !== range.from.getTime()
      ? `${shortDate(range.from)} – ${shortDate(range.to)}`
      : shortDate(range.from)
    : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="enquiries-date-filter"
          className="flex items-center gap-2 h-10 w-full sm:w-auto sm:min-w-[170px] rounded-lg border border-input bg-card px-3 text-sm"
        >
          <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
          <span className={cn("flex-1 text-left truncate", !label && "text-muted-foreground")}>{label || "Select date range"}</span>
          {label && (
            <span
              role="button"
              aria-label="Clear date range"
              onClick={(ev) => {
                ev.stopPropagation();
                onChange(undefined);
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar mode="range" selected={range} onSelect={onChange} numberOfMonths={1} disabled={{ after: new Date() }} />
      </PopoverContent>
    </Popover>
  );
}

export default function EnquiriesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showroomFilter, setShowroomFilter] = useState(ALL);
  const [sourceFilter, setSourceFilter] = useState(ALL);
  const [memberFilter, setMemberFilter] = useState(ALL);
  const [range, setRange] = useState();
  const [showLost, setShowLost] = useState(false);
  const [view, setViewState] = useState(readView);
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState({ open: false, status: "NEW", mode: "existing" });
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const { data: enquiries = [], isLoading } = useQuery({ queryKey: ["enquiries"], queryFn: () => client.get("/enquiries").then(unwrap) });
  const { data: options = { showrooms: [], members: [] } } = useQuery({
    queryKey: ["enquiry-options"],
    queryFn: () => client.get("/enquiries/options").then(unwrap),
  });

  // Optimistic so drag-and-drop feels instant; rolled back if the server refuses.
  const moveMutation = useMutation({
    mutationFn: ({ id, status }) => client.patch(`/enquiries/${id}`, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["enquiries"] });
      const previous = queryClient.getQueryData(["enquiries"]);
      queryClient.setQueryData(["enquiries"], (old = []) => old.map((e) => (e._id === id ? { ...e, status } : e)));
      return { previous };
    },
    onSuccess: (_, { status, label }) => toast.success(`${label} moved to ${STAGE_LABEL[status]}`),
    onError: (err, _, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["enquiries"], ctx.previous);
      toast.error(err?.response?.data?.error?.message || "Couldn't move enquiry");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["enquiries"] }),
  });

  const move = (e, status) => {
    if (e.status !== status) moveMutation.mutate({ id: e._id, status, label: e.code || e.customerName });
  };

  const setView = (v) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* view preference is optional */
    }
  };

  const sourceOptions = useMemo(() => {
    const known = SOURCES.map((s) => s.value);
    const extra = [...new Set(enquiries.map((e) => sourceMeta(e.source)?.value).filter((v) => v && !known.includes(v)))];
    return [...known, ...extra].map((v) => ({ value: v, label: v }));
  }, [enquiries]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const from = range?.from && new Date(range.from).setHours(0, 0, 0, 0);
    const to = range?.from && new Date(range.to || range.from).setHours(23, 59, 59, 999);
    return enquiries
      .filter((e) => {
        const created = new Date(e.createdAt).getTime();
        return (
          (!term || [e.customerName, e.customerPhone, e.code].some((v) => v?.toLowerCase().includes(term))) &&
          (showroomFilter === ALL || e.showroomId === showroomFilter) &&
          (sourceFilter === ALL || sourceMeta(e.source)?.value === sourceFilter) &&
          (memberFilter === ALL || e.assignedUserIds?.includes(memberFilter)) &&
          (!from || (created >= from && created <= to))
        );
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [enquiries, search, showroomFilter, sourceFilter, memberFilter, range]);

  const filtersActive = search || showroomFilter !== ALL || sourceFilter !== ALL || memberFilter !== ALL || range?.from;
  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(1);
  };
  const clearFilters = () => {
    setSearch("");
    setShowroomFilter(ALL);
    setSourceFilter(ALL);
    setMemberFilter(ALL);
    setRange(undefined);
    setPage(1);
  };

  const counts = useMemo(() => {
    const c = { total: enquiries.length };
    enquiries.forEach((e) => (c[e.status] = (c[e.status] || 0) + 1));
    return c;
  }, [enquiries]);
  const lostCount = filtered.filter((e) => e.status === "LOST").length;
  const columns = STAGES.filter((s) => showLost || s.key !== "LOST");

  const listRows = filtered.filter((e) => showLost || e.status !== "LOST");
  const pageCount = Math.max(1, Math.ceil(listRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = listRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const openDialog = (status = "NEW", mode = "existing") => setDialog({ open: true, status, mode });

  return (
    <div data-testid="enquiries-page" className="space-y-5">
      {/* Header: title, stage counts, add button */}
      <div className="flex flex-col 2xl:flex-row 2xl:items-center gap-5">
        <div className="2xl:flex-1 min-w-0">
          <h1 className="font-serif text-3xl sm:text-[2.6rem] leading-tight text-foreground">Enquiry Pipeline</h1>
          <p className="text-muted-foreground mt-1">Track every lead from first contact to won deal.</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
            {STAT_CARDS.map(({ key, label, icon: Icon, tone }) => (
              <div key={key} data-testid={`enquiries-stat-${key}`} className="flex items-center gap-3 bg-card border border-border rounded-2xl px-4 py-3 shadow-[0_1px_2px_rgba(42,33,29,0.04)]">
                <span className={cn("h-10 w-10 rounded-xl flex items-center justify-center shrink-0", tone)}>
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="min-w-0">
                  <p className="font-serif text-2xl leading-none text-foreground">{counts[key] || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1 truncate">{label}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex self-start sm:self-auto shrink-0">
            <Button
              data-testid="add-enquiry-button"
              onClick={() => openDialog()}
              className="rounded-l-full rounded-r-none pl-5 pr-4 bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Enquiry
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="More ways to add an enquiry"
                  data-testid="add-enquiry-menu"
                  className="rounded-r-full rounded-l-none px-2.5 border-l border-accent-foreground/25 bg-accent hover:bg-accent/90 text-accent-foreground"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openDialog("NEW", "existing")}>For an existing customer</DropdownMenuItem>
                <DropdownMenuItem onClick={() => openDialog("NEW", "new")}>For a new customer</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col xl:flex-row xl:items-center gap-3">
        <div className="relative flex-1 xl:max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="enquiries-search-input"
            value={search}
            onChange={(ev) => resetPage(setSearch)(ev.target.value)}
            placeholder="Search by customer name, phone, or enquiry ID…"
            className="w-full h-10 rounded-lg border border-border bg-secondary/70 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 flex-1">
          {options.showrooms.length > 0 && (
            <FilterSelect
              icon={Store}
              value={showroomFilter}
              onChange={resetPage(setShowroomFilter)}
              testId="enquiries-showroom-filter"
              allLabel="All showrooms"
              options={options.showrooms.map((s) => ({ value: s.id, label: s.name }))}
            />
          )}
          <FilterSelect icon={Tag} value={sourceFilter} onChange={resetPage(setSourceFilter)} testId="enquiries-source-filter" allLabel="All sources" options={sourceOptions} />
          {options.members.length > 1 && (
            <FilterSelect
              icon={UserRound}
              value={memberFilter}
              onChange={resetPage(setMemberFilter)}
              testId="enquiries-member-filter"
              allLabel="All team members"
              options={options.members.map((m) => ({ value: m.id, label: m.name }))}
            />
          )}
          <DateRangeFilter range={range} onChange={resetPage(setRange)} />
          {filtersActive && (
            <button data-testid="enquiries-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline px-2 self-start sm:self-auto">
              Clear filters
            </button>
          )}
          <div className="flex items-center gap-2 sm:ml-auto">
            <button
              data-testid="enquiries-toggle-lost"
              aria-pressed={showLost}
              onClick={() => setShowLost((v) => !v)}
              className={cn(
                "h-10 rounded-lg border px-3 text-sm transition-colors whitespace-nowrap",
                showLost ? "border-danger/40 bg-danger/5 text-danger" : "border-border bg-card text-muted-foreground hover:text-foreground",
              )}
            >
              {showLost ? "Hide lost" : "Show lost"} · {lostCount}
            </button>
            <div className="flex rounded-lg border border-border bg-card p-0.5" role="group" aria-label="Layout">
              {[
                { v: "board", Icon: LayoutGrid, label: "Board view" },
                { v: "list", Icon: List, label: "List view" },
              ].map(({ v, Icon, label }) => (
                <button
                  key={v}
                  aria-label={label}
                  aria-pressed={view === v}
                  data-testid={`enquiries-view-${v}`}
                  onClick={() => setView(v)}
                  className={cn(
                    "h-8 w-9 rounded-md flex items-center justify-center transition-colors",
                    view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary",
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {view === "board" ? (
        <div className="flex gap-3.5 overflow-x-auto pb-3 -mx-1 px-1">
          {columns.map((col) => {
            const items = filtered.filter((e) => e.status === col.key);
            const tone = stageTone(col.key);
            const isTarget = dropTarget === col.key && dragging && dragging.status !== col.key;
            return (
              <section
                key={col.key}
                data-testid={`enquiry-column-${col.key}`}
                aria-label={`${col.label} enquiries`}
                onDragOver={(ev) => {
                  if (!dragging) return;
                  ev.preventDefault();
                  setDropTarget(col.key);
                }}
                onDragLeave={(ev) => {
                  if (!ev.currentTarget.contains(ev.relatedTarget)) setDropTarget(null);
                }}
                onDrop={(ev) => {
                  ev.preventDefault();
                  if (dragging) move(dragging, col.key);
                  setDragging(null);
                  setDropTarget(null);
                }}
                className={cn(
                  "flex flex-col w-[272px] shrink-0 xl:flex-1 xl:min-w-[236px] rounded-2xl border p-2.5 transition-shadow",
                  tone.column,
                  isTarget && "ring-2 ring-primary/40",
                )}
              >
                <header className="flex items-center gap-2 px-1.5 pt-1 pb-3">
                  <span className={cn("h-2.5 w-2.5 rounded-full", tone.dot)} />
                  <h2 className="font-serif text-lg text-foreground flex-1">{col.label}</h2>
                  <span className="min-w-[1.75rem] text-center rounded-full bg-card/80 border border-border px-2 py-0.5 text-xs text-muted-foreground">{items.length}</span>
                </header>
                <div className="flex-1 space-y-2.5 min-h-[120px]">
                  {items.map((e) => (
                    <EnquiryCard
                      key={e._id}
                      e={e}
                      onMove={move}
                      dragging={dragging?._id === e._id}
                      onDragStart={setDragging}
                      onDragEnd={() => {
                        setDragging(null);
                        setDropTarget(null);
                      }}
                    />
                  ))}
                  {items.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-8 rounded-xl border border-dashed border-border/80">
                      {isLoading ? "Loading…" : dragging ? "Drop here" : "No enquiries"}
                    </p>
                  )}
                </div>
                {col.key !== "LOST" && (
                  <button
                    data-testid={`enquiry-column-add-${col.key}`}
                    onClick={() => openDialog(col.key)}
                    className={cn("mt-2.5 flex items-center justify-center gap-1.5 rounded-xl border bg-card/60 py-2 text-sm transition-colors", tone.add)}
                  >
                    <Plus className="h-4 w-4" /> Add Enquiry
                  </button>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <section className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/50 text-muted-foreground font-mono-spec text-[11px] uppercase tracking-wider text-left">
                  <th className="py-3 pl-5 pr-3 font-semibold">Enquiry</th>
                  <th className="py-3 px-3 font-semibold">Customer</th>
                  <th className="py-3 px-3 font-semibold">Property</th>
                  <th className="py-3 px-3 font-semibold">Source</th>
                  <th className="py-3 px-3 font-semibold">Showroom</th>
                  <th className="py-3 px-3 font-semibold">Assigned</th>
                  <th className="py-3 px-3 font-semibold">Stage</th>
                  <th className="py-3 pl-3 pr-5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pageRows.map((e) => (
                  <tr
                    key={e._id}
                    data-testid={`enquiry-row-${e._id}`}
                    onClick={() => navigate(`/customers/${e.customerId}`)}
                    className="hover:bg-secondary/40 cursor-pointer transition-colors"
                  >
                    <td className="py-3 pl-5 pr-3 whitespace-nowrap">
                      <p className="font-mono-spec text-xs text-foreground">{e.code || "—"}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(e.createdAt)}</p>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={e.customerName} />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{e.customerName}</p>
                          {e.customerPhone && <p className="text-xs text-muted-foreground">{e.customerPhone}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-foreground/85">
                      {propertyLine(e) || <span className="text-muted-foreground">—</span>}
                      {e.potentialValue ? <p className="text-xs font-semibold text-accent">{formatINR(e.potentialValue)}</p> : null}
                    </td>
                    <td className="py-3 px-3"><SourceTag source={e.source} /></td>
                    <td className="py-3 px-3 text-foreground/85">{e.showroomName || <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-3 px-3 text-foreground/85">
                      {e.assignees?.length ? e.assignees.map((a) => a.name).join(", ") : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-3 px-3"><StagePill status={e.status} /></td>
                    <td className="py-3 pl-3 pr-5 text-right"><EnquiryMenu e={e} onMove={move} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="md:hidden divide-y divide-border">
            {pageRows.map((e) => (
              <li key={e._id} className="p-4" onClick={() => navigate(`/customers/${e.customerId}`)}>
                <div className="flex items-start gap-3">
                  <Avatar name={e.customerName} size="h-10 w-10" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-foreground truncate">{e.customerName}</p>
                      <StagePill status={e.status} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{[e.code, propertyLine(e)].filter(Boolean).join(" · ")}</p>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <SourceTag source={e.source} />
                      <span className="text-muted-foreground">{formatDate(e.createdAt)}</span>
                    </div>
                  </div>
                  <EnquiryMenu e={e} onMove={move} />
                </div>
              </li>
            ))}
          </ul>

          {!isLoading && listRows.length === 0 && (
            <div className="py-14 text-center">
              <p className="text-sm text-muted-foreground">
                {enquiries.length === 0 ? "No enquiries yet. Add your first lead to get started." : "No enquiries match these filters."}
              </p>
              {filtersActive && (
                <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>
              )}
            </div>
          )}

          {listRows.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, listRows.length)} of {listRows.length} enquir
                {listRows.length === 1 ? "y" : "ies"}
              </p>
              <Pagination testId="enquiries-pagination" page={currentPage} pageCount={pageCount} onChange={setPage} />
            </div>
          )}
        </section>
      )}

      <AddEnquiryDialog
        open={dialog.open}
        setOpen={(open) => setDialog((d) => ({ ...d, open }))}
        initialStatus={dialog.status}
        initialMode={dialog.mode}
      />
    </div>
  );
}
